import { ref } from "vue";
import axios from "axios";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import { API_BASE_URL } from "../../../consts";

export function useSnippetShows(token) {
  const toast = useToast();
  const confirm = useConfirm();

  const shows = ref([]);
  const showsLoading = ref(false);

  const fetchShows = async () => {
    try {
      showsLoading.value = true;
      const response = await axios.get(`${API_BASE_URL}/podcast-shows`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { snippetsOnly: true, activeSellerCount: true },
      });
      shows.value = response.data || [];
    } catch (error) {
      console.error("Error fetching snippet shows:", error);
    } finally {
      showsLoading.value = false;
    }
  };

  const removeShow = async (show) => {
    try {
      await axios.delete(`${API_BASE_URL}/snippets/shows/${show.show_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      shows.value = shows.value.filter((s) => s.show_id !== show.show_id);
      toast.add({
        severity: "success",
        summary: "Removed",
        detail: "Show removed from the snippets catalog.",
        life: 4000,
      });
    } catch (error) {
      console.error("Error removing snippet show:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to remove show.",
        life: 4000,
      });
    }
  };

  const confirmRemoveShow = (show) => {
    confirm.require({
      message: `Remove "${show.show_title}" from the snippets catalog?`,
      header: "Remove Confirmation",
      icon: "pi pi-info-circle",
      acceptClass: "p-button-danger",
      accept: () => removeShow(show),
    });
  };

  const generatingOneMoreShowId = ref(null);

  const generateOneMoreSnippet = async (show) => {
    generatingOneMoreShowId.value = show.show_id;
    try {
      await axios.post(
        `${API_BASE_URL}/snippets/shows/${show.show_id}/generate-one`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.add({
        severity: "success",
        summary: "Queued",
        detail: "Snippet generation queued for this show.",
        life: 4000,
      });
    } catch (error) {
      console.error("Error generating one more snippet:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to generate snippet.",
        life: 4000,
      });
    } finally {
      generatingOneMoreShowId.value = null;
    }
  };

  const confirmGenerateOneMoreSnippet = (show) => {
    confirm.require({
      message: `Are you sure you want to generate one more snippet for this show?`,
      header: "Generate Confirmation",
      icon: "pi pi-info-circle",
      accept: () => generateOneMoreSnippet(show),
    });
  };

  const showAddShowDialog = ref(false);
  const availableShowsToAdd = ref([]);
  const availableShowsLoading = ref(false);
  const showToAdd = ref(null);
  const addShowSubmitting = ref(false);

  const openAddShowDialog = async () => {
    showToAdd.value = null;
    showAddShowDialog.value = true;
    availableShowsLoading.value = true;

    try {
      const response = await axios.get(`${API_BASE_URL}/podcast-shows`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const enabledShowIds = new Set(shows.value.map((s) => s.show_id));
      availableShowsToAdd.value = (response.data || []).filter(
        (show) => !enabledShowIds.has(show.show_id)
      );
    } catch (error) {
      console.error("Error fetching podcast shows:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to load shows.",
        life: 4000,
      });
    } finally {
      availableShowsLoading.value = false;
    }
  };

  const confirmAddShow = async () => {
    if (!showToAdd.value) return;

    try {
      addShowSubmitting.value = true;
      await axios.post(
        `${API_BASE_URL}/snippets/shows`,
        { showId: showToAdd.value.show_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.add({
        severity: "success",
        summary: "Added",
        detail:
          "Show added to the snippets catalog. Generation for its 3 newest episodes has been queued.",
        life: 5000,
      });
      showAddShowDialog.value = false;
      await fetchShows();
    } catch (error) {
      console.error("Error adding snippet show:", error);
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to add show.",
        life: 4000,
      });
    } finally {
      addShowSubmitting.value = false;
    }
  };

  return {
    shows,
    showsLoading,
    fetchShows,
    confirmRemoveShow,
    generatingOneMoreShowId,
    confirmGenerateOneMoreSnippet,
    showAddShowDialog,
    availableShowsToAdd,
    availableShowsLoading,
    showToAdd,
    addShowSubmitting,
    openAddShowDialog,
    confirmAddShow,
  };
}
